import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ROUTES from "../../constants/routes";
import storage from "../../util/storage";
import { cleanLink, clearPerSiteCaches } from "../../util/helper";
import { shouldShowSelector } from "./siteSelectorRule";
import AddSiteModal from "./AddSiteModal";
import RemoveSiteModal from "./RemoveSiteModal";
import PageViewsBreakdown from "../views/settings/PageViewsBreakdown";
import { getEffectiveSubscriptionData } from "../../util/entitlements";
import ActionType from "../../resources/enums";
import { persistor } from "../../state_management/reducers";
import { site as siteApi } from "../../services/site";
import { currentUser } from "../../services/auth";

/*
 * Unwraps the JSON-RPC envelope. http.ts's interceptor returns
 * withData(res.data) = { error, data }, and for an RPC call `data` is the
 * envelope { jsonrpc, id, result } - so `res.data` is the ENVELOPE, not the
 * payload. Reading it wrong once already reported a real success as a
 * failure in AddSiteModal; same helper, same reason.
 */
const rpcResult = (res: any): any =>
  res?.data?.result ?? res?.result ?? res?.data;

/*
 * The header's site switcher.
 *
 * 🔴 RENDERS NOTHING NEW WHEN THERE IS ONE SITE. Every existing account owns
 * exactly one, so this must be visually identical to the plain host label it
 * replaces until a second site exists. A selector that appears for everyone
 * would be a UI change shipped to every customer in exchange for a feature
 * almost none of them have yet.
 *
 * 🔴 SWITCHING DOES A FULL PAGE LOAD, deliberately, rather than a client-side
 * route change.
 *
 * The dashboard holds a lot of per-site state that is not owned by the router:
 * the socket is joined to a room keyed by siteId (socket.controller.ts does
 * `socket.join(handshakeData.siteId)`), the bubble simulation holds live
 * nodes, the page-slot model, the sessions list, redux-persist's cache, and
 * `storage.siteId`. Teasing all of that apart is the real work of a smooth
 * in-place switch, and getting it wrong in an ANALYTICS product means showing
 * one site's visitors under another site's name - which is the worst possible
 * bug here, and a silent one.
 *
 * A reload rebuilds every one of those from the new siteId at once. It is a
 * second of latency on an action taken rarely, in exchange for making a whole
 * class of state-bleed impossible. Revisit only with the per-site teardown
 * actually written.
 */
const SiteSelector = () => {
  /*
   * 🔴 AuthReducer, capital A. Header.tsx reads `state?.AuthReducer`, and
   * writing `state.authReducer` here returned undefined - so this rendered an
   * EMPTY STRING where the site name used to be. The header lost its title
   * entirely and no dropdown appeared, which looked like the feature had not
   * deployed rather than like a typo.
   *
   * Nothing caught it: the unit test exercises shouldShowSelector directly and
   * never touches redux, and TypeScript cannot check a key on `any`.
   */
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const [removing, setRemoving] = useState<any>(null);
  const dispatch = useDispatch();
  const rootRef = useRef<HTMLSpanElement | null>(null);

  /*
   * 🔴 SELF-HEAL A SESSION THAT PREDATES `sitesToTrack`.
   *
   * getUserDto now ships the effective site limit, but NOTHING in this app
   * refetches currentUser() on boot - `authData` is restored by
   * redux-persist from whenever the customer last logged in. So shipping the
   * field server-side fixes nothing for anyone already signed in: their
   * persisted authData has no `sitesToTrack`, the limit falls back to 1, and
   * "+ Add a website" stays hidden until they happen to log out and back in.
   *
   * Asking for it ONCE, when it is missing, closes that gap without a
   * re-login. Runs only when the field is absent, so it costs nothing on a
   * session that already has it, and it is deliberately not gated on `open` -
   * the value decides whether the add row renders at all.
   */
  useEffect(() => {
    if (!authReducer?.isLoggedIn) {
      return;
    }
    if (authReducer?.authData?.sitesToTrack !== undefined) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const fresh: any = await currentUser();
        if (!cancelled && fresh?.data) {
          dispatch({ type: ActionType.LOGIN, payload: fresh.data });
        }
      } catch (e) {
        // Non-fatal: the menu still works, it just keeps the conservative
        // limit until the next real login.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authReducer?.isLoggedIn, authReducer?.authData?.sitesToTrack, dispatch]);

  /*
   * Dismiss on an outside click, and on Escape.
   *
   * The filter dropdown gets this free from Bootstrap; this menu is
   * hand-rolled, so without it the panel stayed open while you went off and
   * opened ANOTHER dropdown - two menus on screen at once, which is what
   * Chris hit.
   *
   * 🔴 `mousedown`, not `click`, and the ref covers the TRIGGER as well as the
   * panel. With `click` the document handler and the trigger's own onClick
   * both fire on the same press: the first closes, the second re-opens, and
   * the menu appears not to respond at all.
   */
  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        // claimed here, so the zoomed view's own Escape (a window listener, run after this one) leaves it alone
        e.preventDefault();
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const sites = authReducer?.authData?.sites ?? [];

  /*
   * THE DEFAULT SITE - the one Skydive opens on.
   *
   * 🔴 Deliberately a USER field, not localStorage. `storage.siteId` already
   * exists but means something different: the site you were LAST looking at,
   * which preferChosenSite restores on reload. A default is a stated
   * preference that must survive a new browser, a new machine and a cleared
   * cache, and "opens on my laptop but not my desktop" is a bug report
   * waiting to happen.
   */
  const defaultSiteId = authReducer?.authData?.defaultSiteId;

  /*
   * 🔴 THE ALLOWANCE, NOT JUST THE COUNT.
   *
   * Gating only on sites.length > 1 made the feature invisible to exactly the
   * people paying for it: a customer on a 3-site plan with one site would
   * have had nowhere to discover they could add more.
   *
   * Effective, so a special-offer override counts - see
   * getEffectiveSubscriptionData.
   */
  /*
   * 🔴 authData FIRST, accountRecord only as a fallback.
   *
   * `accountRecord` is populated by ActionType.SUBSCRIPTIONDATA, a DIFFERENT
   * action from LOGIN - so after the full page reload a site switch performs
   * it can be empty. When it was, maxSites fell back to 1, `canAddMore`
   * computed `2 < 1 = false`, and "+ Add a website" disappeared for an
   * account that had room for three. The menu still rendered, because
   * showSelector passes on site count alone, so the only symptom was a
   * missing row.
   *
   * getUserDto now ships the EFFECTIVE limit on the user itself, so it
   * arrives with every login and every currentUser() refresh - the same
   * payload that carries `sites` and `site`, which this component already
   * depends on. The old path is kept underneath it so a session whose DTO
   * predates this change still behaves as before rather than regressing.
   */
  const maxSites =
    authReducer?.authData?.sitesToTrack ??
    getEffectiveSubscriptionData(authReducer?.accountRecord)?.sitesToTrack ??
    1;
  const canAddMore = maxSites === -1 || sites.length < maxSites;
  const showSelector = shouldShowSelector(sites) || maxSites === -1 || maxSites > 1;
  const current = authReducer?.authData?.site;
  // the site as people say it: no "https://", no "www." (Chris, 2026-10-25); the real host is still what every action uses
  const currentHost = cleanLink(current?.host ?? "");

  if (!showSelector) {
    return <>{currentHost}</>;
  }

  /*
   * 🔴 THE SITE COMES FROM REDUX, NOT THE URL.
   *
   * First attempt navigated to /analytics-view/<siteId> and produced a blank
   * "no_route_match" page. That route is registered WITH the :id segment only
   * for a logged-in ADMIN; for a customer it is registered as plain
   * "/analytics-view". Exactly the trap routes.ts already documents for
   * Settings, where links built "/settings/<siteId>" for everyone and matched
   * nothing.
   *
   * AnalyticsView reads authReducer.authData.site._id and then SYNCS
   * storage.siteId from it, so writing storage alone is pointless - the
   * component overwrites it on mount. Redux is the source of truth.
   *
   * So: replace authData.site via the same LOGIN action login itself uses
   * (its reducer already calls syncSiteIdToStorage), flush the persisted
   * store so the new value survives, then reload.
   *
   * The reload is still deliberate - see the note above on per-site state -
   * but it now goes to the route the customer actually has.
   */
  /*
   * Takes the site OBJECT, not just an id. addSite calls this immediately
   * after creating a site, and at that moment the `sites` array captured by
   * this render does not contain it yet - a lookup by id would silently miss
   * and the switch would do nothing.
   */
  /*
   * Set the site Skydive opens on.
   *
   * 🔴 DOES NOT SWITCH TO IT, and does not close the menu. Marking a default
   * is housekeeping, not navigation - someone tidying their list should not
   * be thrown onto a different dashboard (and a full page load, see
   * switchTo) as a side effect. The badge moving is the confirmation.
   */
  const setAsDefault = async (nextSite: any) => {
    const siteId = nextSite?._id;
    if (!siteId || savingDefault || String(defaultSiteId ?? "") === String(siteId)) {
      return;
    }

    setSavingDefault(true);
    try {
      const res: any = await siteApi({
        jsonprc: "2.0",
        method: "setDefaultSite",
        params: { userId: authReducer?.authData?._id, siteId },
        id: 0,
      } as any);

      if (!rpcResult(res)) {
        return;
      }

      /*
       * Re-read the user rather than patching authData locally. The badge is
       * driven by authData.defaultSiteId, and a local patch would show the
       * star moved while the server still held the old value - the same
       * flash-and-revert class of bug switchTo already hit.
       */
      const fresh: any = await currentUser();
      if (fresh?.data) {
        dispatch({ type: ActionType.LOGIN, payload: fresh.data });
      }
    } catch (e) {
      // Non-fatal: the star simply does not move. Nothing else in the menu
      // depends on this having succeeded.
    } finally {
      setSavingDefault(false);
    }
  };

  const switchTo = async (nextSite: any) => {
    const siteId = nextSite?._id;
    if (!siteId || siteId === current?._id) {
      setOpen(false);
      return;
    }

    /*
     * 🔴 STORAGE FIRST, THEN DISPATCH. Order is the whole fix.
     *
     * The LOGIN reducer now calls preferChosenSite, which reads
     * storage.siteId and lets it win over the payload. So dispatching before
     * writing storage meant the reducer read the PREVIOUS site id, found it
     * among the owned sites, and overrode the site I had just selected -
     * then syncSiteIdToStorage wrote the old id straight back.
     *
     * That is precisely the reported symptom: the new site appears for a
     * frame and snaps back. I removed this storage write when rewriting
     * switchTo to go through redux, without noticing the reducer had come to
     * depend on it.
     */
    /*
     * 🔴 CLEAR THE PREVIOUS SITE'S CACHES BEFORE SWITCHING.
     *
     * `pages` lives in localStorage under one global key. Without this the
     * new site's dashboard reads the OLD site's page list and renders it
     * under the new site's name - which is what produced a duplicated /about,
     * slot selection that would not advance, and pages that appeared to have
     * been deleted.
     *
     * Before the storage.set below, so nothing can read a half-switched
     * state: old caches gone, then the new siteId, then the reload.
     */
    clearPerSiteCaches();

    storage.set("siteId", siteId);

    dispatch({
      type: ActionType.LOGIN,
      payload: { ...authReducer.authData, site: nextSite },
    });

    // Without this the reload can race redux-persist's async write and boot
    // on the PREVIOUS site, which then syncs storage back - i.e. the switch
    // silently does nothing.
    try {
      await persistor.flush();
    } catch (e) {
      // fall through - a failed flush must not trap the user mid-switch
    }

    window.location.assign(ROUTES.ANALYTICS_VIEW.replace("/:id", ""));
  };

  return (
    <span className="site-selector" ref={rootRef}>
      <button
        type="button"
        className="site-selector-current"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {currentHost}
        <span className="site-selector-caret" aria-hidden="true" />
      </button>

      {adding && <AddSiteModal onClose={() => setAdding(false)} />}

      {removing && (
        <RemoveSiteModal
          site={removing}
          userId={authReducer?.authData?._id}
          onClose={() => setRemoving(null)}
          onRemoved={async () => {
            /*
             * A full reload, for the same reason switchTo does one: if the
             * REMOVED site was the one open, every piece of per-site state
             * (socket room, bubble nodes, page slots, sessions list) is now
             * pointing at a site that no longer exists. Re-reading the user
             * and reloading rebuilds all of it from whatever the server now
             * says is site[0].
             */
            storage.remove("siteId");
            window.location.assign(ROUTES.ANALYTICS_VIEW.replace("/:id", ""));
          }}
        />
      )}

      {open && (
        /*
         * 🔴 NOT role="listbox"/role="option" any more.
         *
         * Each row now carries its own action buttons, and an `option` is
         * only allowed to contain text - a listbox whose options hold
         * focusable controls is invalid ARIA, and screen readers stop
         * reporting the actions at all. A plain list with aria-current on the
         * open site describes this honestly.
         */
        <ul className="site-selector-list">
          <li className="site-selector-heading" aria-hidden="true">
            <span>Your websites</span>
          </li>

          {sites.map((site: any) => {
            const isCurrent = site._id === current?._id;
            const isDefault = String(defaultSiteId ?? "") === String(site._id);

            return (
              /* 🔴 `is-current` goes on the LI, not the button. The li is the
                 pill (it carries the background and padding, matching
                 .dropdown-menu li), so `li.is-current` is what paints the
                 selected row #fff-on-#08c the way the filter menu does.
                 Putting it on the button meant the selector never matched and
                 the current site looked identical to the others. */
              <li
                key={site._id}
                className={isCurrent ? "is-current" : undefined}
                // the whole pill picks the site; the star and the cross (in the actions column) do their own thing
                onClick={(e) => { if (!(e.target as HTMLElement).closest(".site-selector-actions")) switchTo(site); }}
              >
                <button
                  type="button"
                  aria-current={isCurrent ? "true" : undefined}
                  className={
                    isCurrent
                      ? "site-selector-option is-current"
                      : "site-selector-option"
                  }
                >
                  <span className="site-selector-host">{cleanLink(site.host)}</span>
                  {isDefault && (
                    <span className="site-selector-default-badge">Default</span>
                  )}
                </button>

                {/*
                  THE ACTIONS COLUMN. Fixed width and hard right, so its edge
                  lines up down the whole menu rather than ragging with
                  hostname length.

                  🔴 Only actions with a real backend appear here. Remove,
                  archive, rename and reorder are deliberately ABSENT rather
                  than present-and-disabled: a control that cannot work is
                  worse than no control, and three of the four still need a
                  product decision (archive vs delete) or a new field.
                */}
                <span className="site-selector-actions">
                  <button
                    type="button"
                    className={
                      isDefault
                        ? "site-selector-action is-active"
                        : "site-selector-action"
                    }
                    disabled={isDefault || savingDefault}
                    title={
                      isDefault
                        ? `${cleanLink(site.host)} already opens by default`
                        : `Open ${cleanLink(site.host)} by default`
                    }
                    aria-label={
                      isDefault
                        ? `${cleanLink(site.host)} already opens by default`
                        : `Open ${cleanLink(site.host)} by default`
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      setAsDefault(site);
                    }}
                  >
                    {isDefault ? "\u2605" : "\u2606"}
                  </button>

                  {/* Removing is only offered when there is another site to
                      fall back to. The server refuses the last one anyway
                      (every dashboard read assumes a site exists); hiding it
                      avoids presenting a control that can only fail. */}
                  {sites.length > 1 && (
                    <button
                      type="button"
                      className="site-selector-action"
                      title={`Remove ${cleanLink(site.host)}`}
                      aria-label={`Remove ${cleanLink(site.host)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen(false);
                        setRemoving(site);
                      }}
                    >
                      {"\u00d7"}
                    </button>
                  )}
                </span>
              </li>
            );
          })}

          {/* Only offered when the plan has room. The server refuses anyway;
              hiding it avoids inviting a click that can only fail. */}
          {canAddMore && (
            <li className="site-selector-add">
              <button
                type="button"
                className="site-selector-option site-selector-add-btn"
                onClick={() => {
                  setOpen(false);
                  setAdding(true);
                }}
              >
                + Add a website
              </button>
            </li>
          )}
          {/*
            QUICK LOOK - page views per site, inside the switcher.

            🔴 Rendered ONLY while the menu is open, so the RPC fires on a
            deliberate user action rather than on every dashboard load. The
            switcher sits on the main view; fetching there unprompted would
            put a metrics query on the hot path of the app's busiest screen.

            🔴 It answers the question the switcher itself raises - "which of
            my sites is eating the allowance?" - at the moment someone is
            already thinking about sites. The full breakdown stays in
            Settings; this is the glance.

            🔴 NOT shown for a single-site account: a breakdown of one site is
            just the total with extra steps, and this menu already renders
            nothing new for those accounts by design.
          */}
          {sites.length > 1 && (
            <li className="site-selector-quicklook">
              <PageViewsBreakdown
                userId={authReducer?.authData?._id}
                /* 0 = defer to the server. getPageViewsBySite returns the
                   effective limit (plan + purchased credits), which is the
                   same number Settings divides by. The header has no reliable
                   copy of it - accountRecord can be empty after a reload. */
                limit={0}
                currentSiteId={current?._id}
                compact
              />
            </li>
          )}

        </ul>
      )}
    </span>
  );
};

export default SiteSelector;
